import { useCallback, useEffect, useRef, useState } from 'react';
import {
  VERSION_MIN_CHECK_MS,
  VERSION_POLL_MS,
  VERSION_STORAGE_KEY,
} from '@/constants/version';
import type { BuildInfo, VersionApiResponse } from '@/types/version';

function getVersionEndpointUrl(): string {
  const base = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
  // base is the API root (e.g. /api/v1 or https://host/api/v1), same as axios baseURL
  if (!base) {
    return '/api/v1/version';
  }
  return `${base}/version`;
}

function readStoredBuildInfo(): BuildInfo | null {
  try {
    const raw = localStorage.getItem(VERSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as BuildInfo;
    if (parsed && typeof parsed.buildHash === 'string') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

async function fetchServerBuildInfo(): Promise<BuildInfo | null> {
  const url = getVersionEndpointUrl();
  try {
    const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
    if (!res.ok) {
      return null;
    }
    const body = (await res.json()) as VersionApiResponse;
    if (!body?.success || !body.data?.buildHash) {
      return null;
    }
    if (body.data.buildHash === 'unknown') {
      return null;
    }
    return body.data;
  } catch {
    return null;
  }
}

export function useVersionCheck() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [serverVersion, setServerVersion] = useState<BuildInfo | null>(null);
  const [currentVersion, setCurrentVersion] = useState<BuildInfo | null>(() => readStoredBuildInfo());
  const lastCheckRef = useRef<number>(0);

  const applyServerComparison = useCallback((stored: BuildInfo | null, server: BuildInfo | null) => {
    if (!server) {
      setHasUpdate(false);
      setServerVersion(null);
      return;
    }
    if (!stored) {
      try {
        localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(server));
      } catch {
        /* ignore */
      }
      setCurrentVersion(server);
      setHasUpdate(false);
      setServerVersion(null);
      return;
    }
    setServerVersion(server);
    const mismatch = stored.buildHash !== server.buildHash;
    setHasUpdate(mismatch);
  }, []);

  const checkVersion = useCallback(
    async (force = false) => {
      const now = Date.now();
      if (!force && now - lastCheckRef.current < VERSION_MIN_CHECK_MS) {
        return;
      }
      lastCheckRef.current = now;

      const stored = readStoredBuildInfo();
      setCurrentVersion(stored);
      const server = await fetchServerBuildInfo();
      applyServerComparison(stored, server);
    },
    [applyServerComparison]
  );

  const updateCurrentVersion = useCallback((info: BuildInfo) => {
    localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(info));
    setCurrentVersion(info);
    setHasUpdate(false);
    setServerVersion(null);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void checkVersion(true);
    }, 1000);
    return () => window.clearTimeout(t);
  }, [checkVersion]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void checkVersion(false);
    }, VERSION_POLL_MS);
    return () => window.clearInterval(id);
  }, [checkVersion]);

  return {
    hasUpdate,
    serverVersion,
    currentVersion,
    checkVersion,
    updateCurrentVersion,
  };
}
